
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { calculatePasswordStrength, checkEmailExists, checkUsernameExists, updateUserProfile } from "@/utils/auth";
import { SignUpFormValues, LoginFormValues } from "@/contexts/AuthContext";

export const useAuthentication = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [otpVerificationRequired, setOtpVerificationRequired] = useState(false);
  const [emailForVerification, setEmailForVerification] = useState("");
  const [pendingSignupData, setPendingSignupData] = useState<SignUpFormValues | null>(null);
  
  // Calculate password strength
  const handleCalculatePasswordStrength = (password: string) => {
    const score = calculatePasswordStrength(password);
    setPasswordStrength(score);
  };

  // Handle OTP verification
  const handleOTPVerification = async (otp: string) => {
    setIsSubmitting(true);
    
    try {
      // In a real implementation, this would verify the OTP with Supabase or your auth provider
      // For now, we'll simulate a successful verification after a delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      if (pendingSignupData) {
        // Complete the signup process
        await completeSignupAfterOTP(pendingSignupData);
      }
      
      setOtpVerificationRequired(false);
      setEmailForVerification("");
      setPendingSignupData(null);
      
      toast({
        title: "Verification successful!",
        description: "Your account has been verified successfully.",
        duration: 5000,
      });
      
      // Redirect to home page after successful verification
      navigate("/");
    } catch (error) {
      console.error("OTP verification error:", error);
      
      toast({
        title: "Verification error",
        description: "The code you entered is invalid or has expired. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle resend OTP
  const handleResendOTP = async () => {
    // In a real implementation, this would resend the OTP through your auth provider
    // For now, we'll just simulate a delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    toast({
      title: "Verification code resent",
      description: "Check your email for the new verification code.",
      duration: 3000,
    });
    
    return Promise.resolve();
  };
  
  // Handle cancel OTP verification
  const handleCancelOTP = () => {
    setOtpVerificationRequired(false);
    setEmailForVerification("");
    setPendingSignupData(null);
    setIsSubmitting(false);
  };
  
  // Complete signup after OTP verification
  const completeSignupAfterOTP = async (data: SignUpFormValues) => {
    try {
      // Register with Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            first_name: data.firstName,
            last_name: data.lastName,
            username: data.username
          }
        }
      });
      
      if (authError) {
        throw authError;
      }
      
      // Update the profile with all user data
      if (authData.user) {
        const updateResult = await updateUserProfile(authData.user.id, {
          username: data.username,
          first_name: data.firstName,
          last_name: data.lastName
        });
        
        if (!updateResult) {
          toast({
            title: "Error updating profile",
            description: "Account created but there was a problem saving user data.",
            variant: "destructive",
            duration: 5000,
          });
          return;
        }
      }
      
      toast({
        title: "Account created successfully!",
        description: "Welcome to Propmatch. You can now start investing from $10.",
        duration: 5000,
      });
      
    } catch (error) {
      console.error("Error during signup completion:", error);
      toast({
        title: "Error creating account",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  // Handle signup submission
  const handleSignUp = async (data: SignUpFormValues) => {
    setIsSubmitting(true);
    
    try {
      // Check if username already exists
      const usernameExists = await checkUsernameExists(data.username);
      if (usernameExists) {
        toast({
          title: "Error",
          description: "Username is already taken. Please choose another one.",
          variant: "destructive",
          duration: 5000,
        });
        setIsSubmitting(false);
        return;
      }
      
      // Check if email already exists
      const emailExists = await checkEmailExists(data.email);
      if (emailExists) {
        toast({
          title: "Error",
          description: "Email is already registered. Try logging in or use another email.",
          variant: "destructive",
          duration: 5000,
        });
        setIsSubmitting(false);
        return;
      }
      
      // For demo purposes, always trigger OTP verification 
      // In production, this would depend on your auth configuration
      setOtpVerificationRequired(true);
      setEmailForVerification(data.email);
      setPendingSignupData(data);
      
      // Simulate sending OTP
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast({
        title: "Verification code sent",
        description: "We've sent a verification code to your email.",
        duration: 5000,
      });
      
      setIsSubmitting(false);
    } catch (error) {
      console.error("Error during signup:", error);
      
      toast({
        title: "Error creating account",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
      
      setIsSubmitting(false);
    }
  };

  // Handle login submission
  const handleLogin = async (data: LoginFormValues) => {
    setIsSubmitting(true);
    
    try {
      const { data: authData, error } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password
      });
      
      if (error) {
        console.error("Login error:", error);
        toast({
          title: "Login error",
          description: "Invalid credentials. Please try again.",
          variant: "destructive",
          duration: 5000,
        });
        setIsSubmitting(false);
        return;
      }
      
      toast({
        title: "Logged in!",
        description: "Welcome back to Propmatch.",
        duration: 3000,
      });
      
      setIsSubmitting(false);
      
      // Redirect to home page after successful login
      navigate("/");
    } catch (error) {
      console.error("Error during login:", error);
      
      toast({
        title: "Login error",
        description: "An error occurred. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
      
      setIsSubmitting(false);
    }
  };

  return {
    isSubmitting,
    passwordStrength,
    otpVerificationRequired,
    emailForVerification,
    handleCalculatePasswordStrength,
    handleSignUp,
    handleLogin,
    handleOTPVerification,
    handleResendOTP,
    handleCancelOTP
  };
};
